import { useCallback, useEffect, useRef, useState } from "react";

import { computePosition } from "@floating-ui/dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import Box from "@mui/material/Box";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL as NORMAL_PRIORITY,
  SELECTION_CHANGE_COMMAND as ON_SELECTION_CHANGE,
} from "lexical";
import { createPortal } from "react-dom";

import { FloatingToolbar } from "../FloatingToolbar";

import { usePointerInteractions } from "@/dashboard-design-system/RichTextInput/usePointerInteractions";

type FloatingMenuState = { visible: boolean; x?: number; y?: number };

export type FloatingMenuComponentProps = {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  shouldShow: boolean;
};

const OFFSET = 8;

export function FloatingToolbarPlugin() {
  const ref = useRef<HTMLDivElement>(null);
  const [menuState, setMenuState] = useState<FloatingMenuState>({ visible: false });

  const [editor] = useLexicalComposerContext();
  const pointerState = usePointerInteractions();

  const isPointerUp = pointerState === "up";

  const calculatePosition = useCallback(() => {
    const domSelection = getSelection();
    const domRange = domSelection?.rangeCount !== 0 && domSelection?.getRangeAt(0);

    if (!domRange || !ref.current || !isPointerUp) {
      return setMenuState((s) => ({ ...s, visible: false }));
    }

    computePosition(domRange, ref.current, { placement: "top-start" })
      .then((pos) => {
        setMenuState({ visible: true, x: pos.x, y: pos.y - OFFSET });
      })
      .catch(() => {
        setMenuState((s) => ({ ...s, visible: false }));
      });
  }, [isPointerUp]);

  const handleSelectionChange = useCallback(() => {
    if (editor.isComposing()) {
      return false;
    }

    if (editor.getRootElement() !== document.activeElement) {
      setMenuState((s) => ({ ...s, visible: false }));
      return true;
    }

    const selection = $getSelection();

    if ($isRangeSelection(selection) && !selection.anchor.is(selection.focus)) {
      calculatePosition();
    } else {
      setMenuState((s) => ({ ...s, visible: false }));
    }

    return true;
  }, [editor, calculatePosition]);

  useEffect(() => {
    return editor.registerCommand(ON_SELECTION_CHANGE, handleSelectionChange, NORMAL_PRIORITY);
  }, [editor, handleSelectionChange]);

  useEffect(() => {
    if (!menuState.visible) {
      return;
    }
    const handleScroll = () => calculatePosition();
    document.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", handleScroll, { capture: true });
  }, [menuState.visible, calculatePosition]);

  useEffect(() => {
    if (isPointerUp) {
      editor.getEditorState().read(() => {
        handleSelectionChange();
      });
    }
  }, [isPointerUp, handleSelectionChange, editor]);

  return createPortal(
    <Box
      ref={ref}
      aria-hidden={!menuState.visible}
      data-test="floating-toolbar"
      onMouseDown={(e) => {
        // Prevent the toolbar from stealing focus from the editor
        e.preventDefault();
      }}
      sx={{
        position: "fixed",
        top: menuState.y,
        left: menuState.x,
        zIndex: 1000,
        opacity: menuState.visible ? 1 : 0,
        visibility: menuState.visible ? "visible" : "hidden",
        transition: "opacity 200ms ease-out, translate 200ms ease-out",
        pointerEvents: menuState.visible ? "auto" : "none",
        translate: menuState.visible ? "0 0" : "0 -10px",
      }}
    >
      <FloatingToolbar />
    </Box>,
    document.body
  );
}
